import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import apiClient from '../../../services/apiClient';
import { showConfirm } from '../../../components/ConfirmDialog';

interface Announcement {
  _id: string;
  title: string;
  message: string;
  targetAudience: 'all' | 'members' | 'trainers';
  expirationDate?: string;
  createdAt: string;
}

interface AnnouncementsTabProps {
  gymId: string;
}

const AnnouncementsTab: React.FC<AnnouncementsTabProps> = ({ gymId }) => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    targetAudience: 'all' as 'all' | 'members' | 'trainers',
    expirationDate: '',
  });

  // Fetch announcements for this gym
  const { data: announcements = [], isLoading } = useQuery({
    queryKey: ['gymAnnouncements', gymId],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/gyms/${gymId}/announcements`);
        return response.data || [];
      } catch {
        return [];
      }
    },
  });

  // Create announcement mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiClient.post(`/gyms/${gymId}/announcements`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gymAnnouncements', gymId] });
      setFormData({
        title: '',
        message: '',
        targetAudience: 'all',
        expirationDate: '',
      });
      setShowForm(false);
    },
  });

  // Delete announcement mutation
  const deleteMutation = useMutation({
    mutationFn: async (announcementId: string) => {
      await apiClient.delete(`/announcements/${announcementId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gymAnnouncements', gymId] });
    },
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.title.trim() && formData.message.trim()) {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (announcementId: string) => {
    const announcement = announcements.find((a: Announcement) => a._id === announcementId);
    const announcementTitle = announcement?.title || 'this announcement';
    
    showConfirm({
      title: 'Delete Announcement',
      message: `Are you sure you want to delete "${announcementTitle}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: () => deleteMutation.mutate(announcementId),
    });
  };

  return (
    <div className="space-y-6">
      {/* Create Button */}
      <button
        onClick={() => setShowForm(!showForm)}
        className="flex items-center gap-2 bg-[#23F47C] text-gray-900 px-4 py-2 rounded font-semibold hover:bg-opacity-90"
      >
        <Plus size={20} />
        Create Announcement
      </button>

      {/* Create Form */}
      {showForm && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                placeholder="Announcement title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                placeholder="Announcement message"
                rows={4}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Target Audience
                </label>
                <select
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                >
                  <option value="all">All</option>
                  <option value="members">Members</option>
                  <option value="trainers">Trainers</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Expiration Date
                </label>
                <input
                  type="date"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleInputChange}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:outline-none focus:border-[#23F47C]"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 bg-gray-700 text-white px-4 py-2 rounded font-semibold hover:bg-gray-600 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="flex-1 bg-[#23F47C] text-gray-900 px-4 py-2 rounded font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Announcements List */}
      {isLoading ? (
        <div className="text-gray-400">Loading announcements...</div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No announcements yet
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((announcement: Announcement) => (
            <div
              key={announcement._id}
              className="bg-gray-800 border border-gray-700 rounded-lg p-6"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="text-lg font-semibold text-white">{announcement.title}</h4>
                  <p className="text-gray-400 text-sm">
                    Target: <span className="capitalize">{announcement.targetAudience}</span>
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(announcement._id)}
                  disabled={deleteMutation.isPending}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <Trash2 size={20} />
                </button>
              </div>

              <p className="text-gray-300 mb-3">{announcement.message}</p>

              <div className="flex justify-between text-sm text-gray-500">
                <span>
                  Created: {new Date(announcement.createdAt).toLocaleDateString()}
                </span>
                {announcement.expirationDate && (
                  <span>
                    Expires: {new Date(announcement.expirationDate).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsTab;
